import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { BellRing, RefreshCw, Play, Send, Check, Mail } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { formatDateTime } from '../../i18n/locale';
import { useAuth } from '../../contexts/AuthContext';

// ---- Typen (Spiegel der reminderCron-Strukturen) ----
interface Tier {
  key: string;
  label: string;
  hoursBefore?: number;
  enabled: boolean;
}
interface ReminderSettings {
  enabled: boolean;
  tiers: Tier[];
}
interface UpcomingTier {
  key: string;
  label: string;
  enabled: boolean;
  due: boolean;
  status: 'pending' | 'sent' | 'failed' | 'skipped';
  sent_at: string | null;
  trigger: string | null;
  error: string | null;
}
interface UpcomingAppt {
  id: number;
  patient_name: string;
  patient_email: string | null;
  language: string;
  appointment_date: string;
  start_time: string;
  hours_until: number;
  tiers: UpcomingTier[];
}
interface LogEntry {
  id: number;
  appointment_id: number;
  tier: string;
  recipient: string | null;
  language: string | null;
  status: 'sent' | 'failed' | 'skipped';
  message_id: string | null;
  error: string | null;
  skip_reason: string | null;
  patient_name: string | null;
  appointment_date: string | null;
  start_time: string | null;
  trigger: string;
  sent_by: string;
  sent_at: string;
}

const LANG_LABEL: Record<string, string> = { de: '🇩🇪 DE', en: '🇬🇧 EN', es: '🇪🇸 ES', ru: '🇷🇺 RU' };

function fmtDateTime(ts: string) {
  return formatDateTime(ts, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const STATUS_LABEL_KEYS: Record<string, string> = {
  sent: 'reminders.statusSent',
  failed: 'reminders.statusFailed',
  skipped: 'reminders.statusSkipped',
  pending: 'reminders.statusPending',
};

const LOG_STATUS_FILTERS = [
  { key: 'all', labelKey: 'reminders.filterAll' },
  { key: 'sent', labelKey: 'reminders.statusSent' },
  { key: 'failed', labelKey: 'reminders.statusFailed' },
  { key: 'skipped', labelKey: 'reminders.statusSkipped' },
];

export const RemindersPage: React.FC = () => {
  const { t } = useTranslation();
  // Bearbeiter dürfen ansehen und einzelne Erinnerungen manuell senden.
  // Globale Einstellungen ändern und der Gesamtlauf bleiben Admin-only.
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const langLabel = (code?: string | null) => {
    if (!code) return t('reminders.none');
    return LANG_LABEL[code] || code.toUpperCase();
  };
  const StatusPill = ({ status }: { status: string }) => {
    const MAP: Record<string, string> = {
      sent: 'bg-green-100 text-green-700',
      failed: 'bg-red-100 text-red-700',
      skipped: 'bg-gray-100 text-gray-600',
      pending: 'bg-amber-100 text-amber-700',
    };
    const labelKey = STATUS_LABEL_KEYS[status];
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${MAP[status] || 'bg-gray-100 text-gray-600'}`}>
        {labelKey ? t(labelKey) : status}
      </span>
    );
  };
  const [settings, setSettings] = useState<ReminderSettings | null>(null);
  const [upcoming, setUpcoming] = useState<UpcomingAppt[]>([]);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [running, setRunning] = useState(false);
  const [sendingKey, setSendingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sRes, uRes, lRes] = await Promise.all([
        apiService.getReminderSettings(),
        apiService.getUpcomingReminders(),
        apiService.getReminderLog(),
      ]);
      setSettings((sRes.data as any).data);
      setUpcoming((uRes.data as any).data ?? []);
      setLog((lRes.data as any).data ?? []);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? t('reminders.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const flashNotice = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(null), 4000);
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true);
    setError(null);
    try {
      const payload = {
        enabled: settings.enabled,
        tiers: settings.tiers.map(t => ({ key: t.key, enabled: t.enabled })),
      };
      const res = await apiService.updateReminderSettings(payload);
      setSettings((res.data as any).data);
      flashNotice(t('reminders.settingsSaved'));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? t('reminders.saveError'));
    } finally {
      setSavingSettings(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    setError(null);
    try {
      const res = await apiService.runReminders(false);
      const s = (res.data as any).data;
      flashNotice(t('reminders.runDone', { sent: s.sent, skipped: s.skipped, failed: s.failed }));
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? t('reminders.runError'));
    } finally {
      setRunning(false);
    }
  };

  const sendOne = async (appt: UpcomingAppt, tierKey: string) => {
    const key = `${appt.id}:${tierKey}`;
    setSendingKey(key);
    setError(null);
    try {
      const res = await apiService.sendReminder(appt.id, tierKey);
      if ((res.data as any).success) {
        flashNotice(t('reminders.reminderSent', { name: appt.patient_name }));
      } else {
        setError((res.data as any).message || t('reminders.sendNotPossible'));
      }
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? t('reminders.sendError'));
    } finally {
      setSendingKey(null);
    }
  };

  const setTierEnabled = (key: string, enabled: boolean) => {
    setSettings(prev => prev ? { ...prev, tiers: prev.tiers.map(t => t.key === key ? { ...t, enabled } : t) } : prev);
  };

  const filteredLog = log.filter(e => statusFilter === 'all' || e.status === statusFilter);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <BellRing className="h-5 w-5 text-brand-primary" strokeWidth={1.75} />
          <h1 className="text-lg font-semibold text-brand-text">{t('reminders.title')}</h1>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-accent/10 text-brand-primary uppercase tracking-wider">
            {t('reminders.automaticBadge')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={runNow}
              disabled={running || loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-md bg-brand-primary text-white hover:opacity-90 transition disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
            >
              <Play className={`h-3.5 w-3.5 ${running ? 'animate-pulse' : ''}`} strokeWidth={2} />
              {t('reminders.checkAndSendNow')}
            </button>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-md bg-brand-surface border border-brand-border text-brand-text hover:bg-brand-surface-2 transition disabled:opacity-50 cursor-pointer"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} strokeWidth={2} />
            {t('reminders.reload')}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-[13px] text-red-700">{error}</div>
      )}
      {notice && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-[13px] text-green-700">{notice}</div>
      )}

      {/* Einstellungen */}
      {settings && (
        <div className="bg-brand-surface border border-brand-border rounded-lg p-5">
          <h2 className="text-[13px] font-semibold text-brand-text mb-1">{t('reminders.settings')}</h2>
          <p className="text-[12px] text-brand-text-muted mb-4">
            <Trans
              i18nKey="reminders.settingsIntro"
              components={{ 1: <strong /> }}
            />
          </p>

          <label className={`flex items-center gap-3 mb-4 select-none ${isAdmin ? 'cursor-pointer' : 'cursor-default'}`}>
            <input
              type="checkbox"
              checked={settings.enabled}
              disabled={!isAdmin}
              onChange={e => setSettings({ ...settings, enabled: e.target.checked })}
              className="h-4 w-4 rounded border-brand-border text-brand-primary focus:ring-brand-primary/30 disabled:opacity-60"
            />
            <span className="text-[13px] text-brand-text font-medium">{t('reminders.automaticActive')}</span>
          </label>

          <div className="space-y-2 mb-4">
            <p className="text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold">{t('reminders.leadTimes')}</p>
            {settings.tiers.map(tier => (
              <label key={tier.key} className={`flex items-center gap-3 select-none ${isAdmin ? 'cursor-pointer' : 'cursor-default'} ${settings.enabled ? '' : 'opacity-50'}`}>
                <input
                  type="checkbox"
                  checked={tier.enabled}
                  disabled={!settings.enabled || !isAdmin}
                  onChange={e => setTierEnabled(tier.key, e.target.checked)}
                  className="h-4 w-4 rounded border-brand-border text-brand-primary focus:ring-brand-primary/30 disabled:opacity-60"
                />
                <span className="text-[13px] text-brand-text">
                  {tier.label} <span className="text-brand-text-muted">{t('reminders.beforeAppointment')}</span>
                </span>
              </label>
            ))}
          </div>

          {isAdmin ? (
            <button
              onClick={saveSettings}
              disabled={savingSettings}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-[12px] font-medium rounded-md bg-brand-primary text-white hover:opacity-90 transition disabled:opacity-50 cursor-pointer"
            >
              <Check className="h-3.5 w-3.5" strokeWidth={2} />
              {savingSettings ? t('reminders.saving') : t('reminders.save')}
            </button>
          ) : (
            <p className="text-[11px] text-brand-text-muted italic">{t('reminders.adminOnlyHint')}</p>
          )}
        </div>
      )}

      {/* Bevorstehende Termine */}
      <div className="bg-brand-surface border border-brand-border rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-brand-border flex items-center gap-2">
          <Mail className="h-4 w-4 text-brand-primary" strokeWidth={1.75} />
          <h2 className="text-[13px] font-semibold text-brand-text">{t('reminders.upcomingTitle')}</h2>
          <span className="text-[11px] text-brand-text-muted ml-auto">{t('reminders.upcomingCount', { count: upcoming.length })}</span>
        </div>
        {loading && upcoming.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-brand-text-muted">{t('reminders.loadingShort')}</div>
        ) : upcoming.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-brand-text-muted">{t('reminders.noUpcoming')}</div>
        ) : (
          <div className="divide-y divide-brand-border">
            <div className="hidden md:grid grid-cols-[1fr_140px_70px_70px_1fr] gap-3 px-5 py-2 text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold bg-brand-surface-2">
              <span>{t('reminders.colPatient')}</span>
              <span>{t('reminders.colAppointment')}</span>
              <span>{t('reminders.colLanguage')}</span>
              <span className="text-right">{t('reminders.colIn')}</span>
              <span>{t('reminders.colReminders')}</span>
            </div>
            {upcoming.map(appt => (
              <div key={appt.id} className="grid md:grid-cols-[1fr_140px_70px_70px_1fr] gap-3 px-5 py-3 items-center text-[12px]">
                <div className="min-w-0">
                  <div className="font-medium text-brand-text truncate">{appt.patient_name}</div>
                  <div className="text-brand-text-muted text-[11px] truncate">{appt.patient_email || t('reminders.noEmailValue')}</div>
                </div>
                <div className="text-brand-text">
                  {formatDateTime(`${appt.appointment_date}T00:00:00`, { day: '2-digit', month: '2-digit' })} · {appt.start_time}
                </div>
                <div className="text-brand-text">{langLabel(appt.language)}</div>
                <div className="text-right text-brand-text-muted">{t('reminders.hoursUntil', { hours: appt.hours_until })}</div>
                <div className="flex flex-wrap gap-2">
                  {appt.tiers.map(tier => {
                    const key = `${appt.id}:${tier.key}`;
                    const sending = sendingKey === key;
                    return (
                      <div key={tier.key} className="inline-flex items-center gap-1.5 bg-brand-surface-2 rounded-md px-2 py-1">
                        <span className="text-[11px] text-brand-text-muted">{tier.label}:</span>
                        {tier.status === 'sent' ? (
                          <span className="text-[11px] text-green-700" title={tier.sent_at ? fmtDateTime(tier.sent_at) : ''}>{t('reminders.tierSent')}</span>
                        ) : tier.status === 'skipped' ? (
                          <span className="text-[11px] text-gray-500">{t('reminders.tierSkipped')}</span>
                        ) : (
                          <span className="text-[11px] text-amber-600">{tier.due ? t('reminders.tierDue') : t('reminders.tierPlanned')}</span>
                        )}
                        <button
                          onClick={() => sendOne(appt, tier.key)}
                          disabled={sending || !appt.patient_email}
                          title={!appt.patient_email ? t('reminders.noEmailTitle') : t('reminders.sendNowTitle')}
                          className="inline-flex items-center gap-1 text-[11px] text-brand-primary hover:underline disabled:opacity-40 disabled:no-underline cursor-pointer disabled:cursor-not-allowed"
                        >
                          <Send className={`h-3 w-3 ${sending ? 'animate-pulse' : ''}`} strokeWidth={2} />
                          {sending ? t('reminders.sending') : t('reminders.send')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Protokoll */}
      <div className="bg-brand-surface border border-brand-border rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-brand-border flex items-center gap-3 flex-wrap">
          <BellRing className="h-4 w-4 text-brand-primary" strokeWidth={1.75} />
          <h2 className="text-[13px] font-semibold text-brand-text">{t('reminders.logTitle')}</h2>
          <div className="flex items-center gap-1 bg-brand-surface-2 border border-brand-border rounded-md p-0.5 ml-auto">
            {LOG_STATUS_FILTERS.map(f => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`px-2.5 py-1 text-[11px] font-medium rounded transition cursor-pointer ${
                  statusFilter === f.key ? 'bg-brand-primary text-white shadow-sm' : 'text-brand-text-muted hover:text-brand-text'
                }`}
              >
                {t(f.labelKey)}
              </button>
            ))}
          </div>
        </div>
        {filteredLog.length === 0 ? (
          <div className="px-5 py-10 text-center text-[13px] text-brand-text-muted">{t('reminders.noLog')}</div>
        ) : (
          <div className="divide-y divide-brand-border">
            <div className="hidden md:grid grid-cols-[150px_1fr_80px_70px_90px_1fr] gap-3 px-5 py-2 text-[10px] uppercase tracking-wider text-brand-text-muted font-semibold bg-brand-surface-2">
              <span>{t('reminders.colTimestamp')}</span>
              <span>{t('reminders.colRecipient')}</span>
              <span>{t('reminders.colLeadTime')}</span>
              <span>{t('reminders.colLanguage')}</span>
              <span>{t('reminders.colTrigger')}</span>
              <span>{t('reminders.colStatusDetails')}</span>
            </div>
            {filteredLog.map(e => (
              <div key={e.id} className="grid md:grid-cols-[150px_1fr_80px_70px_90px_1fr] gap-3 px-5 py-3 items-center text-[12px]">
                <div className="text-brand-text font-mono text-[11px]">{fmtDateTime(e.sent_at)}</div>
                <div className="min-w-0">
                  <div className="text-brand-text truncate">{e.patient_name || '—'}</div>
                  <div className="text-brand-text-muted text-[11px] truncate">{e.recipient || '—'}</div>
                </div>
                <div className="text-brand-text">{e.tier === '24h' ? t('reminders.tier24h') : e.tier === '7d' ? t('reminders.tier7d') : e.tier}</div>
                <div className="text-brand-text">{langLabel(e.language)}</div>
                <div className="text-brand-text-muted text-[11px]">
                  {e.trigger === 'manual' ? (
                    <span title={e.sent_by}>{t('reminders.triggerManual')}<br /><span className="text-[10px]">{e.sent_by}</span></span>
                  ) : t('reminders.triggerAutomatic')}
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <StatusPill status={e.status} />
                  {e.error && <span className="text-[11px] text-red-600 truncate" title={e.error}>{e.error}</span>}
                  {e.skip_reason && <span className="text-[11px] text-brand-text-muted truncate" title={e.skip_reason}>{e.skip_reason}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RemindersPage;
